import { useMemo } from "react";
import { marked } from "marked";
import LegalPage from "./LegalPage";

const markdown = `# Privacy Policy

*Last updated: March 2026*

## 1. Introduction

Welcome to ***SEND IT*** ("we," "our," or "us"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our motorsport prediction game service.

## 2. Information We Collect

We collect information that you provide directly to us, including:

- **Account information:** email address, name, and password (or authentication credentials via Google sign-in)
- **Profile information:** display name, favourite team, and avatar selection
- **Prediction data:** your driver matchup picks, pole position predictions, pole margin predictions, and sprint podium predictions
- **League participation data:** leagues you create or join, league roles, and invite codes you generate
- **Feedback and communications:** messages you submit through the in-app feedback form or when you contact us for support

We also automatically collect certain technical information when you use the service, including session authentication data and device/browser information necessary to provide the service.

## 3. How We Use Your Information

We use the information we collect to:

- Provide, maintain, and improve our services
- Process your predictions and calculate scores
- Display leaderboards and league standings
- Send you notifications about race deadlines, matchup availability, and results (if you have opted in to email notifications)
- Respond to your feedback, questions, and support requests
- Monitor and analyse usage trends to improve the service
- Enforce our Terms of Service and protect against misuse

## 4. Third-Party Services

We use the following third-party services to operate ***SEND IT***:

- **Supabase:** cloud database and authentication provider — stores your account data, predictions, and league information
- **Google OAuth:** if you choose to sign in with Google, we receive your name and email address from your Google account
- **Resend:** email delivery service — used to send you notifications and communications

These providers act as data processors on our behalf and are contractually obligated to protect your information. We do not sell, trade, or rent your personal information to any third party.

## 5. Information Sharing

We may share your information only in the following circumstances:

- **Leaderboards and leagues:** your display name, avatar, favourite team, scores, and ranking are visible to other users on public leaderboards and within leagues you join
- **League members:** your display name and prediction performance are visible to other members of leagues you participate in
- **Legal obligations:** to comply with applicable law, regulation, legal process, or enforceable governmental request
- **Protection of rights:** to protect the rights, privacy, safety, or property of ***SEND IT***, our users, or the public

We will never share your email address, password, or private account details with other users.

## 6. Data Security

We implement appropriate technical and organisational security measures to protect your personal information, including row-level security policies that ensure users can only access their own prediction data. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.

## 7. Data Retention

We retain your personal information for as long as your account is active or as needed to provide you our services. If you wish to delete your account and associated data, please contact us at hello@sendit-app.com and we will process your request within 30 days.

## 8. Your Rights

Depending on your location, you may have the right to:

- Access the personal information we hold about you
- Request correction of inaccurate data
- Request deletion of your data
- Object to or restrict processing of your data
- Data portability (receive your data in a structured, commonly used format)
- Withdraw consent at any time where processing is based on consent

To exercise any of these rights, please contact us at hello@sendit-app.com.

## 9. Cookies and Tracking

We use essential cookies and local storage to maintain your session and authentication state. We do not use third-party tracking cookies, advertising cookies, or behavioural analytics. Any usage data collected is used solely to improve the service.

## 10. Children's Privacy

Our service is not intended for children under 16 years of age. We do not knowingly collect personal information from children under 16. If you are a parent or guardian and believe your child has provided us with personal information, please contact us at hello@sendit-app.com and we will take steps to delete such information.

## 11. International Users

***SEND IT*** is operated from Australia. If you are accessing the service from outside Australia, please be aware that your information may be transferred to, stored, and processed in Australia. By using the service, you consent to the transfer of your information to Australia and acknowledge that your data will be handled in accordance with this Privacy Policy and applicable Australian privacy law.

## 12. Changes to This Policy

We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the updated Privacy Policy within the application and updating the "Last updated" date above. Your continued use of the service after any changes constitutes acceptance of the updated Privacy Policy.

## 13. Contact Us

If you have any questions about this Privacy Policy or wish to exercise your data rights, please contact us at hello@sendit-app.com.`;

export default function Privacy() {
  const html = useMemo(() => marked.parse(markdown) as string, []);
  return <LegalPage title="Privacy Policy" content={html} />;
}
